/**
 * Value formatting utilities for export.
 *
 * Provides consistent formatting for different data types:
 * - money: decimal without currency symbol (e.g., "15000.00")
 * - percent: decimal with % suffix (e.g., "18.50%")
 * - date: ISO format or period label as-is
 * - number: numeric value as string
 */

import type { ColumnDefinition } from './types';
import { formatDateISO } from '@/utils/date';

/**
 * Formats a value based on the specified format type.
 *
 * @param value - The raw value to format
 * @param format - The format type from ColumnDefinition
 * @returns Formatted string representation
 */
export function formatValue(
    value: unknown,
    format?: ColumnDefinition['format']
): string {
    // Handle null/undefined
    if (value === null || value === undefined) {
        return '';
    }

    switch (format) {
        case 'money':
            return formatMoney(value);
        case 'percent':
            return formatPercent(value);
        case 'date':
            return formatDate(value);
        case 'number':
            return formatNumber(value);
        default:
            // No format specified - convert to string
            return String(value);
    }
}

/**
 * Formats a money value as decimal without currency symbol.
 *
 * @param value - Numeric value (can be number or string)
 * @returns Formatted string with 2 decimal places (e.g., "15000.00")
 */
function formatMoney(value: unknown): string {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) {
        return '';
    }
    return num.toFixed(2);
}

/**
 * Formats a percent value with % suffix.
 *
 * @param value - Numeric value (can be number or string)
 * @returns Formatted string with % suffix (e.g., "18.50%")
 */
function formatPercent(value: unknown): string {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) {
        return '';
    }
    return `${num.toFixed(2)}%`;
}

/**
 * Formats a date value.
 *
 * If the value is already a string (e.g., period label), returns it as-is.
 * If it's a Date object, formats to ISO date string (YYYY-MM-DD).
 *
 * @param value - Date value (Date object or string)
 * @returns Formatted date string
 */
function formatDate(value: unknown): string {
    if (value instanceof Date) {
        return formatDateISO(value);
    }
    // Assume string date or period label - return as-is
    return String(value);
}

/**
 * Formats a number value.
 *
 * @param value - Numeric value
 * @returns String representation of the number
 */
function formatNumber(value: unknown): string {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) {
        return '';
    }
    return String(num);
}
